import React from 'react';
import { Box, Pagination, Tooltip, Typography } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTranslation } from 'react-i18next';
import DashboardIcon from '../../../assets/svg/dashboard.svg?react';
import BallotIcon from '../../../assets/svg/ballot.svg?react';
import styles from './DocumentsBlock.module.scss';

/** Same breakpoint as `breakpoints.$mobile` in scss/settings/_breakpoints.scss. */
const MOBILE_QUERY = '(max-width: 767px)';

export type DocumentsActionsProps = {
  /** Prefix for the element ids, e.g. 'employeeProfile' → 'employeeProfileCardBtn'. */
  idPrefix: string;
  cardView: boolean;
  onCardView: () => void;
  onListView: () => void;
  /** e.g. while a workflow is open with breadcrumbs in the card view. */
  listViewDisabled?: boolean;
  showPagination: boolean;
  page: number;
  totalPages: number;
  onPageChange: (event: React.ChangeEvent<unknown>, page: number) => void;
  paginationDisabled?: boolean;
};

/**
 * Documents actions block of the profile pages, built like the Document Vault one: the
 * pagination and the card / list view switcher. `standalone` draws it as a rounded block of its
 * own (above the workflow cards); otherwise it is the top of a `DocumentsBlock`, attached to the
 * table under it.
 */
export const DocumentsActions = ({
  idPrefix,
  cardView,
  onCardView,
  onListView,
  listViewDisabled,
  showPagination,
  page,
  totalPages,
  onPageChange,
  paginationDisabled,
  standalone,
}: DocumentsActionsProps & { standalone?: boolean }) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(MOBILE_QUERY);

  return (
    <Box
      id={`${idPrefix}DocumentsBlockHeader`}
      className={`${styles.block_header}${standalone ? ` ${styles.block_header_standalone}` : ''}`}
    >
      <Box className={styles.header_controls}>
        {showPagination && (
          <Box id={`${idPrefix}PaginationBox`} className={styles.header_pagination}>
            <Typography className={styles.pages_value} variant='caption'>
              {t('labels.pageOfTotal', { page, total: totalPages })}
            </Typography>
            <Pagination
              id={`${idPrefix}PagePagination`}
              disabled={paginationDisabled}
              count={totalPages}
              page={page}
              onChange={onPageChange}
              siblingCount={isMobile ? 0 : 1}
              boundaryCount={1}
              className={styles.pagination}
            />
          </Box>
        )}
        <Box
          className={styles.card_list_view_container}
          role='group'
          aria-label={t('labels.documentsView')}
        >
          <Tooltip title={t('buttons.card')} placement='top' arrow>
            <button
              id={`${idPrefix}CardBtn`}
              type='button'
              aria-pressed={cardView}
              className={`${styles.variant_btn}${cardView ? ` ${styles.variant_btn_active}` : ''}`}
              onClick={onCardView}
            >
              <DashboardIcon id={`${idPrefix}CardBtnDashboardIcon`} />
              <span id={`${idPrefix}CardBtnText`} className={styles.visually_hidden}>
                {t('buttons.card')}
              </span>
            </button>
          </Tooltip>
          <Tooltip title={t('buttons.list')} placement='top' arrow>
            {/* The span keeps the tooltip working while the button is disabled. */}
            <span className={styles.variant_btn_wrapper}>
              <button
                id={`${idPrefix}ListBtn`}
                type='button'
                aria-pressed={!cardView}
                disabled={listViewDisabled}
                className={`${styles.variant_btn}${!cardView ? ` ${styles.variant_btn_active}` : ''}`}
                onClick={onListView}
              >
                <BallotIcon id={`${idPrefix}ListBtnBallotIcon`} />
                <span id={`${idPrefix}ListBtnText`} className={styles.visually_hidden}>
                  {t('buttons.list')}
                </span>
              </button>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Documents actions + table as one rounded block (the actions are attached to the table).
 * Without actions the children are rendered as they are.
 */
const DocumentsBlock = ({
  actions,
  children,
}: {
  actions?: DocumentsActionsProps;
  children: React.ReactNode;
}) => {
  if (!actions) {
    return <>{children}</>;
  }
  return (
    <Box id={`${actions.idPrefix}DocumentsBlock`} className={styles.documents_block}>
      <DocumentsActions {...actions} />
      <Box className={styles.block_content}>{children}</Box>
    </Box>
  );
};

export default DocumentsBlock;
